import {
  useGetAccountPermissionsQuery,
  useUpdateIndividualAdvisorPermissionMutation,
} from '../store/permissionsApi';
import { Typography, Alert, Paper, Stack } from '@mui/material';
import PermissionsSkeleton from './PermissionsSkeleton';
import AdvisorPermissionsListComponent from './advisorPermissionsList';
import { useLocalization } from '@/hooks/useLocalization';
import { useImpersonation } from '@/hooks/useImpersonation';

interface PermissionsComponentProps {
  accountId: string | number;
}

const PermissionsComponent = ({ accountId }: PermissionsComponentProps) => {
  const { t } = useLocalization();

  const {
    data: accountPermissions,
    error: accountPermissionsError,
    isLoading: isAccountPermissionsLoading,
  } = useGetAccountPermissionsQuery({ accountId: accountId.toString() });

  const [updateIndividualPermission, { isLoading: isUpdatingIndividual }] =
    useUpdateIndividualAdvisorPermissionMutation();

  const { isImpersonating } = useImpersonation();

  const handleAdvisorToggle = async (advisorId: number) => {
    if (!accountPermissions || isImpersonating) return;
    const advisor = accountPermissions.Advisors.find(
      (a) => a.AdvisorId === advisorId,
    );
    if (!advisor) return;

    try {
      await updateIndividualPermission({
        accountId: accountId.toString(),
        advisorId,
        payload: {
          ...advisor,
          IsAllowedToSee: !advisor.IsAllowedToSee,
        },
      }).unwrap();
    } catch (error) {
      console.error('Failed to update individual permission', error);
    }
  };

  if (isAccountPermissionsLoading) return <PermissionsSkeleton />;
  if (accountPermissionsError)
    return (
      <Alert severity='error'>{t('permissions.errorLoadingSettings')}</Alert>
    );
  if (!accountPermissions || accountPermissions.AdvisorsCount === 0)
    return (
      <Alert severity='info'>{t('permissions.noAdvisorsConnected')}</Alert>
    );

  return (
    <Paper
      elevation={0}
      sx={{ p: 3 }}
    >
      <Stack spacing={2}>
        <Typography variant='h6'>
          {t('permissions.advisorPermissions')}
        </Typography>
        <AdvisorPermissionsListComponent
          advisors={accountPermissions.Advisors}
          onAdvisorToggle={(advisorId) => {
            void handleAdvisorToggle(advisorId);
          }}
          isDisabled={isImpersonating || isUpdatingIndividual}
        />
      </Stack>
    </Paper>
  );
};

export default PermissionsComponent;
